import { useState, useEffect, useRef, useCallback } from "react";

export type SocketStatus = "connected" | "disconnected" | "connecting";

interface UseSocketOptions {
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  reconnectOnClose?: boolean;
  pauseOnHidden?: boolean;
}

interface UseSocketReturn {
  status: SocketStatus;
  sendMessage: (message: string) => void;
  socket: WebSocket | null;
  onMessage: (callback: (message: string) => void) => void;
  error: Event | null;
  reconnect: () => void;
  isPageVisible: boolean;
}

const DEFAULT_OPTIONS: UseSocketOptions = {
  reconnectInterval: 1000, // Base delay of 1 second
  maxReconnectAttempts: 5,
  reconnectOnClose: true,
  pauseOnHidden: true, // Pause reconnection attempts when page is hidden
};

export const useSocket = (url: string, options: UseSocketOptions = {}): UseSocketReturn => {
  const [status, setStatus] = useState<SocketStatus>("disconnected");
  const [isPageVisible, setIsPageVisible] = useState<boolean>(true);
  const messageCallbackRef = useRef<(message: string) => void>(() => {});
  const [error, setError] = useState<Event | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const optionsRef = useRef({ ...DEFAULT_OPTIONS, ...options });

  const connect = useCallback(() => {
    if (!url) {
      setStatus("disconnected");
      return;
    }
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    setStatus("connecting");
    const ws = new WebSocket(url);

    ws.onopen = () => {
      setStatus("connected");
      reconnectAttemptsRef.current = 0;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };

    ws.onclose = () => {
      setStatus("disconnected");

      if (
        optionsRef.current.reconnectOnClose &&
        reconnectAttemptsRef.current < optionsRef.current.maxReconnectAttempts! &&
        (!optionsRef.current.pauseOnHidden || isPageVisible)
      ) {
        // Calculate delay with exponential backoff
        const delay = optionsRef.current.reconnectInterval! * 2 ** reconnectAttemptsRef.current;

        reconnectTimeoutRef.current = window.setTimeout(() => {
          reconnectAttemptsRef.current += 1;
          connect();
        }, delay);
      }
    };

    ws.onerror = (event) => {
      setError(event);
      setStatus("disconnected");
    };

    ws.onmessage = (event) => {
      messageCallbackRef.current(event.data);
    };

    socketRef.current = ws;
  }, [url, isPageVisible]);

  const reconnect = useCallback(() => {
    if (!url) return;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    reconnectAttemptsRef.current = 0;
    connect();
  }, [connect, url]);

  useEffect(() => {
    if (url) {
      connect();
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connect, url]);

  const sendMessage = useCallback((message: string) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(message);
    }
  }, []);

  const onMessage = useCallback((callback: (message: string) => void) => {
    messageCallbackRef.current = callback;
  }, []);

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = document.visibilityState === "visible";
      setIsPageVisible(isVisible);

      // If page becomes visible and we're disconnected, try to reconnect
      if (isVisible && status === "disconnected" && url) {
        reconnect();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [status, reconnect, url]);

  return {
    status,
    sendMessage,
    socket: socketRef.current,
    onMessage,
    error,
    reconnect,
    isPageVisible,
  };
};
